import { Box, Flex, Input, Center, Portal } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";
import Card from "@root/components/enrollment-card/card";
import { useTranslation } from "react-i18next";
import { StyledSelect } from "@root/components/common/select/StyledSelect";
import { useNavigate } from "react-router-dom";
import { COURSE_ENROLLMENT_STATUS } from "@root/constants";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useEffect, useRef, useState } from "react";
import { ROUTE_NAMES } from "@root/constants/navigation-routes";
import CustomSelect from "../common/CustomSelect";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE_COURSES } from "@root/constants";
import { Spinner } from "@chakra-ui/react";
import useMediaQ from "@root/hooks/useMediaQ";
import MobileCourses from "./MobileCourses";
import SearchInput from "@root/components/common/SearchInput";
import { useJournalistCoursesTourGuide } from "@root/hooks/useJournalistCoursesTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const CoursesPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<string>("");
  const [selectedEnrollmentStatus, setSelectedEnrollmentStatus] = useState<string>("");

  // Single state object — start + end updated atomically → one render → one effect → one API call.
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({ start: null, end: null });

  // Suppresses the initial-mount fire of the unified effect so we don't
  // make a bare /courses call before the user has interacted with any filter.
  // (Courses are still fetched on mount via the separate initialFetchRef guard.)
  const isFirstRender = useRef(true);

  const { sm, isMobileView } = useMediaQ();
  const isMobile = !sm;
  const {
    journalistCoursesTourGuide,
    courseCardRef,
    isMobile: isMobileTour,
  } = useJournalistCoursesTourGuide();

  const {
    fetchCourses,
    categoriesData,
    fetchCategories,
    coursesData,
    search,
    setSearch,
    setPage,
    page,
    pageCount,
    getCoursesLoading,
  } = useCourseManagementContext();

  // ─── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    // Fetch categories once and load the initial unfiltered course list.
    fetchCategories();
    fetchCourses({});

    return () => {
      // Issue 2 fix: reset context state when leaving the page so coming back
      // always starts on page 1 with no stale search or filtered data visible.
      setPage(1);
      setSearch("");
      clearDates();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Unified filter effect ───────────────────────────────────────────────────
  // Fires whenever any filter or pagination dep changes AFTER the initial mount.
  // Issue 1 fix: skip the first render so the mount call above is the only
  // initial fetch — avoids a duplicate bare /courses call on load.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const categoryIds: Set<string> = new Set();
    const subcategoryIds: string[] = [];
    selectedCategories?.forEach((val) => {
      if (typeof val === "string") {
        if (val?.includes(":")) {
          const parts = val?.split(":");
          const parentId = parts[0];
          const subId = parts[1];
          if (parentId) categoryIds?.add(parentId);
          if (subId) subcategoryIds?.push(subId);
        } else {
          categoryIds?.add(val);
        }
      }
    });

    const payload: any = {};
    if (categoryIds.size > 0) {
      payload.categoryId = Array.from(categoryIds).join(",");
    }
    if (subcategoryIds.length > 0) {
      payload.subcategoryId = subcategoryIds.join(",");
    }
    if (selectedRequirement) {
      payload.requirement = selectedRequirement;
    }
    if (dateRange.start && dateRange.end) {
      payload.enrollmentPeriodStart = dateRange.start;
      payload.enrollmentPeriodEnd = dateRange.end;
    }
    if (selectedEnrollmentStatus) {
      payload.enrollmentStatus = selectedEnrollmentStatus;
    }
    fetchCourses(payload);
  }, [selectedCategories, selectedRequirement, dateRange, search, page, selectedEnrollmentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const clearDates = () => {
    setDateRange({ start: null, end: null });
  };

  const handleDateChange = (date: any) => {
    if (!Array.isArray(date)) return;
    const [start, end] = date;
    if (start && end) {
      setDateRange({ start, end });
    }
    // Single date: date-field.tsx handles the picker UX internally — no API call.
  };

  // ─── Derived values ──────────────────────────────────────────────────────────

  const dateFieldValue =
    dateRange.start && dateRange.end
      ? [dateRange.start, dateRange.end]
      : undefined;

  const categoriesOptions = categoriesData?.rows?.map((item: any) => ({
    label: item?.title,
    value: String(item?.id),
    children: item?.children?.map((subcat: any) => ({
      label: subcat?.title,
      value: String(subcat?.id),
    })),
  }));

  const getSelectedCategoryIds = () => {
    return selectedCategories
      ?.filter((val) => typeof val === "string")
      ?.map((val) => {
        if (val.includes(":")) {
          return val.split(":").pop();
        }
        return val;
      });
  };

  const selectedCategoryIds = getSelectedCategoryIds();
  const filteredCourses =
    selectedCategoryIds.length > 0
      ? coursesData?.rows?.filter((course: any) => {
          if (course.subcategory && course.subcategory.id) {
            return (
              selectedCategoryIds?.includes(String(course?.category?.id)) ||
              selectedCategoryIds?.includes(String(course?.subcategory?.id))
            );
          } else {
            return selectedCategoryIds?.includes(String(course?.category?.id));
          }
        })
      : coursesData?.rows;

  const hasActiveFilters =
    selectedCategories?.length > 0 ||
    selectedRequirement ||
    dateRange.start ||
    dateRange.end ||
    search;

  const noCoursesFound =
    !getCoursesLoading && (!filteredCourses || filteredCourses.length === 0);

  function renderEmptyState() {
    return (
      <Center flexDirection="column" minH="500px" py={10} px={4} width="100%">
        <Text
          fontSize="24px"
          fontWeight={600}
          color="#2F2F34"
          mb={2}
          textAlign="center"
        >
          {t("coursesPage.noCoursesFound")}
        </Text>
        <Text
          fontSize="16px"
          fontWeight={400}
          color="#798692"
          textAlign="center"
          maxW="500px"
          lineHeight="1.6"
        >
          {hasActiveFilters
            ? t("coursesPage.noCoursesForFilters")
            : t("coursesPage.noCoursesAvailable")}
        </Text>
      </Center>
    );
  }

  function renderCourses() {
    if (noCoursesFound) {
      return renderEmptyState();
    }

    // Desktop layout (original)
    return (
      <Flex
        as="div"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            base: "1fr",
            sm: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
            "2xl": "repeat(4, 1fr)",
          },
          gap: "15px",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {filteredCourses?.map((course: any, index: number) =>
          index === 0 ? (
            <Box key={index} ref={courseCardRef}>
              <Card
                course={course}
                onClick={() =>
                  navigate(
                    `${ROUTE_NAMES.JOURNALIST_COURSE_DETAIL}/${course?.id}`,
                  )
                }
              />
            </Box>
          ) : (
            <Card
              key={index}
              course={course}
              onClick={() =>
                navigate(
                  `${ROUTE_NAMES.JOURNALIST_COURSE_DETAIL}/${course?.id}`,
                )
              }
            />
          ),
        )}
      </Flex>
    );
  }

  // Return mobile component if on mobile
  if (isMobileView && isMobile) {
    return (
      <MobileCourses
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        selectedRequirement={selectedRequirement}
        setSelectedRequirement={setSelectedRequirement}
        categoriesOptions={categoriesOptions}
        filteredCourses={filteredCourses}
        getCoursesLoading={getCoursesLoading}
        page={page}
        pageCount={pageCount}
        setPage={setPage}
        setSearch={setSearch}
        dateFieldValue={dateFieldValue}
        handleDateChange={handleDateChange}
        clearDates={clearDates}
      />
    );
  }

  // Desktop layout
  return (
    <Box
      py={4}
      px={8}
      bg="#fafbfc"
      minW={"full"}
      height={"100% !important"}
      className="ali"
    >
      <Box minW={"full"} mx="auto" height={"100% !important"}>
        <Text fontWeight={600} fontSize={"24px"} color={"#2F2F34"} mb={4}>
          {t("coursesPage.getStarted")}
        </Text>
        {/* <Text fontSize="16px" fontWeight={400} textColor={"#798692"} mb={6}>
          {t("coursesPage.browseAndEnroll")}
        </Text> */}

        {/* Desktop Layout */}
        <Flex pr={5} justifyContent={"space-between"} mb={5}>
          <Box data-tour-item="journalist-courses-search">
            <SearchInput
              maxW="340px"
              placeholder={t("coursesPage.searchPlaceholder")}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  setPage(1);
                }
              }}
            />
          </Box>
          <Flex gap={4}>
            <Box data-tour-item="journalist-courses-category-filter">
              <StyledSelect
                placeholder={t("coursesPage.categories")}
                data={categoriesOptions}
                selectedValues={selectedCategories}
                onChange={setSelectedCategories}
                mt={10}
              />
            </Box>

            {/* <Box
              position={"relative"}
              height={"fit-content"}
              data-tour-item="journalist-courses-requirement-filter"
            >
              <CustomSelect
                options={requirementOptions}
                value={selectedRequirement}
                onChange={setSelectedRequirement}
                placeholder={t("coursesPage.requirement")}
                width="160px"
                height="44px"
              />
            </Box>

            <Box
              position={"relative"}
              height={"fit-content"}
              width={"230px"}
              data-tour-item="journalist-courses-enrollment-filter"
            >
              <DateField
                height="44px"
                py={0}
                placeholder={t("translate.enrollmentPeriod")}
                value={dateFieldValue}
                onChange={handleDateChange}
                onClear={clearDates}
                disabled={false}
                isISO
                range
                inputWidth="100%"
              />
            </Box> */}

            <Box
              position={"relative"}
              height={"fit-content"}
              data-tour-item="journalist-courses-enrollment-status-filter"
            >
              <CustomSelect
                options={[
                  { label: t("coursesPage.enrolled"), value: COURSE_ENROLLMENT_STATUS.ENROLLED },
                  { label: t("coursesPage.notEnrolled"), value: "not_enrolled" },
                ]}
                value={selectedEnrollmentStatus}
                onChange={setSelectedEnrollmentStatus}
                placeholder={t("coursesPage.enrollmentStatus")}
                
                height="44px"
              />
            </Box>
          </Flex>
        </Flex>

        {/* Loading State */}
        {getCoursesLoading ? (
          <Flex justify="center" align="center" minHeight="200px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <>
            {renderCourses()}

            {/* Pagination */}
            {pageCount > 0 && (
              <Box mt={1} display="flex" justifyContent="center">
                <Paginate
                  marginTop={6}
                  page={page}
                  count={pageCount}
                  pageSize={RECORDS_PER_PAGE_COURSES}
                  onPageChange={(newPage: number) => {
                    setPage(newPage);
                  }}
                  variant="ghost"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {!isMobileTour && !journalistCoursesTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={journalistCoursesTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}

      {!isMobileTour &&
        journalistCoursesTourGuide.isActive &&
        journalistCoursesTourGuide.currentStepData && (
          <TourGuide
            isActive={journalistCoursesTourGuide.isActive}
            currentStep={journalistCoursesTourGuide.currentStepData}
            currentStepIndex={journalistCoursesTourGuide.currentStep}
            totalSteps={journalistCoursesTourGuide.totalSteps}
            onNext={journalistCoursesTourGuide.nextStep}
            onBack={journalistCoursesTourGuide.previousStep}
            onClose={journalistCoursesTourGuide.stopTour}
            targetRef={
              journalistCoursesTourGuide.currentStepData.id ===
              "journalist-courses-course-card"
                ? courseCardRef
                : undefined
            }
            targetSelector={
              journalistCoursesTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
    </Box>
  );
};

export default CoursesPage;
